import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, EditorialButton } from "@/lib/editorial";
import { ChevronLeft, AlertTriangle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { agentsApi } from "@/lib/api";

export default function NewAgent() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [liAtCookie, setLiAtCookie] = useState("");
  const [jsessionid, setJsessionid] = useState("");
  const [linkedinEmail, setLinkedinEmail] = useState("");
  const [maturity, setMaturity] = useState("cold");
  const [error, setError] = useState("");

  const createMut = useMutation({
    mutationFn: () => agentsApi.create({ name, liAtCookie, jsessionid, linkedinEmail, maturity }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents"] });
      toast(`Agent "${name}" created.`);
      nav("/admin/agents");
    },
    onError: (e) => setError(e.message || "Create failed."),
  });

  function submit(e) {
    e.preventDefault();
    if (!name.trim()) { setError("Agent name is required."); return; }
    if (!liAtCookie.trim()) { setError("Paste the li_at session cookie for this LinkedIn account."); return; }
    createMut.mutate();
  }

  return (
    <div className="px-8 lg:px-14 py-12 max-w-[1100px]">
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-6">
        <Link to="/admin/agents" className="hover:text-[var(--ink)] flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Agents
        </Link>
        <span>/</span>
        <span className="text-[var(--ink)]">New agent</span>
      </div>

      <PageHeader
        eyebrow="Onboarding"
        tape="Connect"
        rest="an account"
        sub="Paste the LinkedIn session cookies for this account and the engine takes it from there — research, classify, invite, and message, all driven by live availability, not a schedule."
        testId="new-agent-title"
      />

      {error && (
        <div
          className="mb-8 inline-flex items-center gap-2 px-4 py-2.5 bg-rose-50 border border-rose-300 text-rose-700 text-[12px]"
          data-testid="new-agent-error"
        >
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-10">
        <Field label="Agent name" hint="Internal identifier. Must be unique.">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="new-agent-name"
            placeholder="e.g. Daniel Voss"
            className="w-full bg-transparent border-0 border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-3 text-[20px] font-display"
          />
        </Field>

        <Field label="LinkedIn session cookie" hint="Log into this LinkedIn account in a normal browser, open dev tools → Application → Cookies → linkedin.com, and copy the li_at value. No password is stored.">
          <textarea
            value={liAtCookie}
            onChange={(e) => setLiAtCookie(e.target.value)}
            data-testid="new-agent-li-at-cookie"
            placeholder="AQEDAV..."
            rows={2}
            className="w-full bg-transparent border-0 border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-3 text-[13px] font-mono resize-none"
          />
        </Field>

        <Field label="JSESSIONID cookie" hint="From the same Cookies panel. Required for LinkedIn search/discovery to work — without it, search requests redirect-loop.">
          <input
            value={jsessionid}
            onChange={(e) => setJsessionid(e.target.value)}
            data-testid="new-agent-jsessionid"
            placeholder="ajax:1234567890123456789"
            className="w-full bg-transparent border-0 border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-3 text-[13px] font-mono"
          />
        </Field>

        <Field label="LinkedIn account email" hint="Optional — for display only, so you can tell agents apart in Settings.">
          <input
            value={linkedinEmail}
            onChange={(e) => setLinkedinEmail(e.target.value)}
            data-testid="new-agent-linkedin-email"
            placeholder="e.g. milana@unidex.co"
            className="w-full bg-transparent border-0 border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-3 text-[15px] font-mono"
          />
        </Field>

        <Field label="Account maturity" hint="Sets initial safety limits. Tunable after creation.">
          <Select value={maturity} onValueChange={setMaturity}>
            <SelectTrigger className="rounded-none border-0 border-b border-[var(--ink)]/30 focus:ring-0 h-12 text-[15px]" data-testid="new-agent-maturity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cold">cold — 2 DM, 5 invite / day</SelectItem>
              <SelectItem value="warm">warm — 5 DM, 10 invite / day</SelectItem>
              <SelectItem value="mature">mature — 15 DM, 20 invite / day</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <div className="flex items-center gap-4 pt-4 border-t border-[var(--rule-light)]">
          <EditorialButton as="button" type="submit" data-testid="new-agent-submit">
            Create agent
          </EditorialButton>
          <Link
            to="/admin/agents"
            className="text-[12px] uppercase tracking-[0.22em] text-[var(--muted-light)] hover:text-[var(--ink)]"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 border-b border-[var(--rule-light)] pb-8">
      <div className="md:col-span-4">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mb-2">
          Field
        </div>
        <div className="font-display text-[22px] leading-tight">{label}</div>
        <p className="text-[12px] text-[var(--muted-light)] mt-2 max-w-xs leading-relaxed">{hint}</p>
      </div>
      <div className="md:col-span-8">{children}</div>
    </div>
  );
}
